import { Link } from "react-router-dom";
import { useMemo } from "react";
import styled from "styled-components";
import { CardComp } from "../../../components";
import InvestorPageShell from "../../../components/Investor/InvestorPageShell";
import InvestorDashboardSkeleton from "../../../components/Investor/InvestorDashboardSkeleton";
import KycBanner from "../../../components/Investor/KycBanner";
import InvestorStatusBadge from "../../../components/Investor/InvestorStatusBadge";
import ActivityFeed from "../../../components/shared/ActivityFeed";
import { useInvestorSession } from "../../../hooks/useInvestorSession";
import { formatMoney } from "../../../utils/currencyFormat";
import {
  useListInvestorInvestmentRequestsQuery,
  useListInvestorOpportunitiesQuery,
} from "../../../store/investor/investorInvestmentsApi";
import { useListMarketplaceOrdersQuery } from "../../../store/investor/investorMarketplaceApi";
import { useListRealEstateRequestsQuery } from "../../../store/investor/investorRealEstateApi";
import {
  mapInvestorOpportunityListItem,
  mapInvestorRequestToListShape,
} from "../../../utils/investorInvestmentsAdapter";

const Hero = styled.div`
  border-radius: 15px;
  padding: 1.75rem 1.5rem;
  margin-bottom: 1.75rem;
  color: #fff;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    color-mix(in srgb, var(--color-primary) 70%, var(--color-secondary)) 55%,
    var(--color-secondary) 100%
  );
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);

  @media (min-width: 768px) {
    padding: 2.25rem 2rem;
  }
`;

const SectionTitle = styled.h5`
  font-weight: 600;
  color: #383838;
  margin-bottom: 1.15rem;
`;

const SummaryCard = styled(CardComp).attrs({ noDefaultPadding: true })`
  && {
    border: none;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    border-radius: 15px;
    height: 100%;
    margin-bottom: 0;
    padding: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    .card-body {
      padding: 1.15rem 1.25rem;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    }
  }
`;

const QuickActionLink = styled(Link)`
  text-decoration: none;
  display: block;
  height: 100%;

  .quick-action-card {
    border: 1px solid #e5e5e5;
    border-radius: 15px;
    height: 100%;
    margin-bottom: 0;
    padding: 0 !important;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    .card-body {
      padding: 1.15rem 1.25rem;
    }

    &:hover {
      border-color: var(--color-primary-soft);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
    }
  }
`;

export default function InvestorDashboard() {
  const { profile, kycStatus, isMember, isRealInvestor } = useInvestorSession();

  const { data: apiRequests, isLoading: isLoadingRequests } =
    useListInvestorInvestmentRequestsQuery({ per_page: 20 });
  const { data: apiOpportunities, isLoading: isLoadingOpportunities } =
    useListInvestorOpportunitiesQuery({ per_page: 3 });
  const { data: marketplaceOrdersPage, isLoading: isLoadingOrders } =
    useListMarketplaceOrdersQuery({ per_page: 1 });
  const { data: realEstatePage, isLoading: isLoadingRealEstate } =
    useListRealEstateRequestsQuery({ per_page: 20 });

  const isDashboardLoading =
    isLoadingRequests || isLoadingOpportunities || isLoadingOrders || isLoadingRealEstate;

  const investments = useMemo(
    () => (apiRequests?.items || []).map(mapInvestorRequestToListShape).filter(Boolean),
    [apiRequests?.items]
  );
  const realEstateRequests = realEstatePage?.items || [];
  const marketplaceOrderCount = marketplaceOrdersPage?.pagination?.total ?? 0;
  const inspectionPending = realEstateRequests.filter((r) =>
    ["inspection_scheduled", "submitted", "awaiting_contact", "in_progress"].includes(r.status)
  ).length;

  const portfolioValue = investments.reduce((s, r) => s + (r.payload?.totalNgn || 0), 0);
  const firstName = profile?.first_name || profile?.full_name?.split(" ")[0] || "Investor";

  const summaryCards = [
    {
      label: "Portfolio value",
      value: formatMoney(portfolioValue.toFixed(2)),
      hint: portfolioValue > 0 ? "Across all holdings" : "Start your first investment",
    },
    {
      label: "Active investments",
      value: String(investments.length),
      hint: investments.length ? "Submitted requests" : "No active positions yet",
    },
    {
      label: "Marketplace orders",
      value: String(marketplaceOrderCount),
      hint: marketplaceOrderCount ? "Orders placed" : "Your orders will appear here",
    },
    {
      label: "Property requests",
      value: String(realEstatePage?.pagination?.total ?? realEstateRequests.length),
      hint:
        inspectionPending > 0
          ? `${inspectionPending} need follow-up`
          : realEstateRequests.length
            ? "Interest requests submitted"
            : "No property requests yet",
    },
  ];

  const membershipAction =
    isMember && !isRealInvestor
      ? { label: "Open Member Portal", to: "/member/dashboard", desc: "Go to your membership home" }
      : { label: "Join Cooperative", to: "/member-signup", desc: "Start membership registration" };

  const quickActions = [
    { label: "Investments", to: "/invest/investments", desc: "Explore opportunities" },
    { label: "Marketplace", to: "/invest/marketplace", desc: "Order products" },
    membershipAction,
    { label: "Real estate", to: "/invest/real-estate", desc: "Property requests" },
    { label: "Profile", to: "/invest/profile?tab=verification", desc: "Verify identity" },
  ];

  const recommended = (apiOpportunities?.items || [])
    .map(mapInvestorOpportunityListItem)
    .filter(Boolean)
    .slice(0, 3);

  return (
    <InvestorPageShell
      title="Dashboard"
      subtitle="Track investments, marketplace orders, and requests in one place."
      backTo={null}
    >
      {isDashboardLoading ? (
        <InvestorDashboardSkeleton />
      ) : (
        <>
      <Hero>
        <p className="small mb-1 opacity-75">Investor dashboard</p>
        <h2 className="fw-bold mb-2">Welcome back, {firstName}</h2>
        <p className="mb-0 opacity-90 small">
          Your portfolio overview, quick actions, and recommended opportunities.
        </p>
      </Hero>

      <KycBanner status={kycStatus} redirectTo="/invest/dashboard" />

      <SectionTitle>Your portfolio at a glance</SectionTitle>
      <div className="row g-3 mb-4">
        {summaryCards.map((c) => (
          <div key={c.label} className="col-6 col-lg-3">
            <SummaryCard cardClassName="h-100">
              <p className="text-muted small mb-1">{c.label}</p>
              <h4 className="fw-bold mb-1" style={{ color: "#383838" }}>
                {c.value}
              </h4>
              <p className="text-muted small mb-0">{c.hint}</p>
            </SummaryCard>
          </div>
        ))}
      </div>

      <SectionTitle>Quick actions</SectionTitle>
      <div className="row g-3 mb-4" data-tour="tour-quick-actions">
        {quickActions.map((a) => (
          <div key={a.label} className="col-6 col-md-4 col-xl">
            <QuickActionLink to={a.to}>
              <CardComp noDefaultPadding cardClassName="quick-action-card h-100 border-0 shadow-sm">
                <p className="fw-bold mb-1" style={{ color: "var(--color-primary)" }}>
                  {a.label}
                </p>
                <p className="text-muted small mb-0">{a.desc}</p>
              </CardComp>
            </QuickActionLink>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <SectionTitle className="mb-0">Recent activity</SectionTitle>
            <Link
              to="/invest/notifications"
              className="small fw-semibold"
              style={{ color: "var(--color-secondary)" }}
            >
              Notifications →
            </Link>
          </div>
          <CardComp noDefaultPadding cardClassName="border-0 shadow-sm rounded-4 mb-0" cardBodyClassName="p-3 p-md-4">
            <ActivityFeed
              guard="investor"
              perPage={8}
              showFilters
              showPagination={false}
              groupByDate
              compact
              emptyMessage="No activity yet. Explore investments to get started."
            />
          </CardComp>
        </div>
        <div className="col-lg-5">
          <SectionTitle>Recommended opportunities</SectionTitle>
          <div className="d-flex flex-column gap-3">
            {recommended.length === 0 ? (
              <p className="text-muted small mb-0">No opportunities available right now.</p>
            ) : (
              recommended.map((opp) => (
                <CardComp key={opp.id} noDefaultPadding cardClassName="border-0 shadow-sm rounded-4 mb-0" cardBodyClassName="p-3 p-md-4">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <InvestorStatusBadge status={opp.category} backgroundColor="var(--color-secondary)" label={opp.category} />
                    <span className="small fw-semibold" style={{ color: "var(--color-primary)" }}>
                      {opp.expectedReturnPct}%
                    </span>
                  </div>
                  <h6 className="fw-bold mb-1">{opp.name}</h6>
                  <p className="small text-muted mb-2">
                    {formatMoney(opp.unitPriceNgn)} / unit · {opp.durationMonths} months
                  </p>
                  <Link
                    to={`/invest/investments/${opp.id}`}
                    className="small fw-semibold"
                    style={{ color: "var(--color-secondary)" }}
                  >
                    View opportunity →
                  </Link>
                </CardComp>
              ))
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </InvestorPageShell>
  );
}
